<script setup>
import { storeToRefs } from "pinia";
import { computed, onMounted, watch } from "vue";
import { useActiveDeviceRenderStore } from "@/stores/activeDeviceRender";
import { useRoute, useRouter } from "vue-router";
import HeatMap from "@/components/HeatMap.vue";
import { gql } from "graphql-tag";
import { useQuery } from "@vue/apollo-composable";
import AnswerHistoryItem from "@/views/preview/sections/AnswerHistoryItem.vue";

const router = useRouter();
const route = useRoute()
const activeDeviceRender = useActiveDeviceRenderStore();
const {historyData} = storeToRefs(activeDeviceRender)
const props = defineProps({
  devicePreview: Object,
    mcp: {
    type: Boolean,
    default: false,
  }
});

const devices = [
  {id: 'iphone_14', name: 'Mobile'},
  {id: 'ipad_pro_11', name: 'Tablet'},
  {id: 'chrome_1280', name: 'Desktop'}
]

const selectedDeviceName = computed(() => {
  const device = devices.find(d => d.id === route.params.deviceId);
  return device ? device.name : 'Select Device';
});

function changeDevice(deviceId) {
  router.push({
    name: route.name,
    params: {...route.params, deviceId},
    query: route.query
  });
}

watch(historyData, (historyData) => {
  highlightHistory()
})
const updatedPromptsIds = computed(() => {

  let history = activeDeviceRender.page.history || [];
  let pageId = activeDeviceRender.historyData?.pageId;
  history = history.find((h) => h.id === pageId);
  if (history) {
    let updatedPrompts = history.tracked_prompts?.updated || {}

    let updatedPromptIds = []

    updatedPromptIds.push(...(updatedPrompts.updated_answer || []))
    updatedPromptIds.push(...(updatedPrompts.answered_and_verified || []))
    updatedPromptIds.push(...(updatedPrompts.not_answered || []))
    updatedPromptIds.push(...(updatedPrompts.llm_not_answered || []))
    updatedPromptIds.push(...(updatedPrompts.llm_not_verified || []))
    return [...new Set(updatedPromptIds)]
  }

  return [];
});

const GET_PROMPTS_DETAILS = gql`
  query GetPromptsDetails($promptIds: [bigint!], $pageVersionId: bigint!, $pageVersionIdOld: bigint!) {
    websites_trackedquestion(where: {id: {_in: $promptIds}}) {
      id
      question
      tracked_answers(where: {page_version_id: {_in: [$pageVersionId, $pageVersionIdOld]}}, order_by: {page_version_id: desc}) {
        id
        result
        page_version_id
        openai_status
        openai_result
        gemini_status
        gemini_result
      }
    }
  }
`;

const promptsDetailsVariables = computed(() => ({
  promptIds: updatedPromptsIds.value,
  pageVersionId: activeDeviceRender.historyData?.pageId,
  pageVersionIdOld: activeDeviceRender.historyData?.pageIdOld
}));

const {result: promptsDetailsResult} = useQuery(GET_PROMPTS_DETAILS, promptsDetailsVariables, () => ({
  enabled: updatedPromptsIds.value.length > 0 && !!activeDeviceRender.historyData?.pageId,
}));


function getChangedAnswerForQuestion(questionId) {
  const pageVersionId = activeDeviceRender.historyData?.pageId;
  const pageVersionIdOld = activeDeviceRender.historyData?.pageIdOld;
  let q = promptsDetailsResult.value.websites_trackedquestion.find((question) => question.id === questionId)
  if (!q){
    return {}
  }
  const currentEntry = q.tracked_answers.find(a => a.page_version_id === pageVersionId);
  const oldEntry = q.tracked_answers.find(a => a.page_version_id === pageVersionIdOld);
  if (!(currentEntry && oldEntry)) {
    return {}
  }

  return {
    id: q.id,
    question: q.question,
    dateOld: historyData.value.dateOld,
    dateNew: historyData.value.dateNew,
    currentEntry: currentEntry,
    oldEntry: oldEntry,
  };
}

function getChangedAnswerForQuestions(questionIds) {
  let r = [];
  for (let id of questionIds) {
    r.push(getChangedAnswerForQuestion(id))
  }
  return r
}

const categorizedPrompts = computed(() => {
  let history = activeDeviceRender.page.history || [];
  let pageId = activeDeviceRender.historyData?.pageId;
  history = history.find((h) => h.id === pageId);
  if (!history || !promptsDetailsResult.value) return {};

  const updatedPrompts = history.tracked_prompts?.updated || {};

  return {
    updated_answer: {
      label: 'Updated Answer',
      icon: 'fa-robot',
      color: 'text-primary',
      description: 'Accepted answer changed',
      prompts: getChangedAnswerForQuestions(updatedPrompts.updated_answer)
    },
    llm_updated_answer: {
      label: 'LLM Updated Answer',
      icon: 'fa-robot',
      color: 'text-primary',
      description: 'LLM answer changed',
      prompts: getChangedAnswerForQuestions(updatedPrompts.llm_updated_answer)
    },
    answered_and_verified: {
      label: 'Answered and Verified',
      icon: 'fa-check-double',
      color: 'text-success',
      description: 'Questions became answered',
      prompts: getChangedAnswerForQuestions(updatedPrompts.answered_and_verified)
    },
    not_answered: {
      label: 'Not Answered',
      icon: 'fa-circle-xmark',
      color: 'text-danger',
      description: 'Question is no longer answered',
      prompts: getChangedAnswerForQuestions(updatedPrompts.not_answered)
    },
    llm_not_answered: {
      label: 'Not Answered By LLM',
      icon: 'fa-circle-xmark',
      color: 'text-danger',
      description: 'Question is no longer answered by LLM',
      prompts: getChangedAnswerForQuestions(updatedPrompts.llm_not_answered)
    },
    llm_not_verified: {
      label: 'LLM Not Verified',
      icon: 'fa-triangle-exclamation',
      color: 'text-warning',
      description: 'Question is no longer correctly answered by AI',
      prompts: getChangedAnswerForQuestions(updatedPrompts.llm_not_verified)
    }
  };
});

const totalCategorizedPrompts = computed(() => {
  return Object.values(categorizedPrompts.value).reduce((acc, cat) => acc + cat.prompts.length, 0);
});

const historyMarks = computed(() => {
  let result = [];
  let id = 0;

  if (activeDeviceRender.historyData) {

    for (let diff_mark of activeDeviceRender.historyData.diff_marks) {
      id += 1;
      let newText = diff_mark.box.text;
      if (!newText) {
        newText = '[resized element]'
      }
      result.push({
        id: id,
        type: diff_mark.type,
        node: diff_mark.box.node,
        oldText: diff_mark.parent.text,
        newText: newText,
      })
    }
  }
  return result;
})

function shouldTruncate(text, limit = 150) {
  return text && text.length > limit;
}

function toggleField(obj, field) {
  obj[field] = !obj[field];
}

function highlightHistory() {

  if (!props.devicePreview) {
    return;
  }
  let ratio = props.devicePreview.ratio();

  if (activeDeviceRender.historyData) {
    let newHighlights = []
    let newHighlightsParent = []
    let id = 0;
    for (let diff_mark of activeDeviceRender.historyData.diff_marks) {
      id += 1;
      let box = {
        left: diff_mark.box.left,
        top: diff_mark.box.top,
        width: diff_mark.box.width,
        height: diff_mark.box.height,
      }
      let parentBox = {
        left: diff_mark.parent.left,
        top: diff_mark.parent.top,
        width: diff_mark.parent.width,
        height: diff_mark.parent.height,
      }
      let q = {section: 'history', i: id, history: route.query.history}
      q['i'] = id;

      newHighlights.push({
        id: id,
        i: 1,
        box: box,
        query: q,
        'style': {
          'visibility': 'visible',
          'left': `${box.left * ratio}px`,
          'top': `${box.top * ratio}px`,
          'width': `${box.width * ratio}px`,
          'height': `${box.height * ratio}px`,
        },
        'variant': {
          'type': '',
          'style': 'box',
          'sentiment': 'positive',
          'color': 'red',
        },
      })
      if (diff_mark.parent && diff_mark.parent.left) {
        newHighlightsParent.push({
          id: `o_${id}`,
          box: parentBox,
          query: q,
          'style': {
            'visibility': 'visible',
            'left': `${parentBox.left * ratio}px`,
            'top': `${parentBox.top * ratio}px`,
            'width': `${parentBox.width * ratio}px`,
            'height': `${parentBox.height * ratio}px`,
          },
          'variant': {
            'type': '',
            'style': 'box',
            'sentiment': 'positive',
            'color': 'red',
          },
        })
      }
    }
    newHighlights.sort((a, b) => {
      if (a.box.top > b.box.top) {
        return 1
      }
      if (a.box.top < b.box.top) {
        return -1
      }
      return 0
    })
    props.devicePreview.showBoxes(newHighlights, newHighlightsParent)

    let index = parseInt(route.query.i, 10);
    let historyItem = historyMarks.value[index - 1]
    if (historyItem) {
      // highlightDetails.value.content.title = 'Update Found';
      // highlightDetails.value.content.text_1 = '';
      // highlightDetails.value.content.text_2 = '';
      // devicePreview.value.highlightEntries(historyItem.occurrences, {broken_link: historyItem.url})
      props.devicePreview.scrollToEntryIndex(index - 1)
    }
  }
}

const history = computed(() => {

  let result = [];
  for (let entry of activeDeviceRender.page.history) {
    if (!entry.device_renders) {
      continue;
    }
    let r = entry.device_renders.find((item) => {
      return item.device === activeDeviceRender.deviceRender.deviceId
    })
    if (r) {
      let h = {}
      Object.assign(h, r);
      h.pageId = entry.id;
      h.trackedPrompts = entry.tracked_prompts;
      result.push(h)
    }
  }
  result.reverse()
  return result;
})
onMounted(() => {
  if (!route.query.history) {
    let query = Object.assign({}, route.query);
    query['history'] = 1;
    router.replace({query: query})
  }

  props.devicePreview.showBoxes([])
});
</script>

<template>
  <div class="mt-3 p-2" v-if="!mcp">
    <div style="position:relative">
      <heat-map :history="history" :show-prompts="!route.path.includes('/mcp/')"></heat-map>
    </div>
  </div>

  <div class="mt-2">
    <template v-if="route.query.history && historyData">
      <div class="ms-2 mb-2">
        <span class="badge text-bg-warning mb-2">History:</span>&nbsp;
        <template v-if="historyData.dateOld">
          <span class="badge bg-black-50">{{ $filters.dateFilter(historyData.dateOld) }}</span> -&gt;
          <span class="badge bg-black-50">{{ $filters.dateFilter(historyData.dateNew) }}</span>
        </template>
      </div>

      <div v-if="totalCategorizedPrompts > 0">
        <div class="px-3 mb-2 mt-4">
          <small class="text-uppercase fw-bold text-muted" style="letter-spacing: 0.05rem; font-size: 0.7rem;">Updated
            Answers</small>
        </div>
        <div class="list-group list-group-flush mb-3">
          <div class="list-group-item border-0" v-for="(category, key) in categorizedPrompts" :key="key">
            <template v-if="category.prompts.length > 0">
              <div class="issue-header rounded collapsed" data-bs-toggle="collapse"
                   :data-bs-target="'#issue-panel-history-prompts-' + key">
                <div class="d-flex align-items-center flex-grow-1 issue-text text-truncate">
                  <span class="fa me-1 issue-icon-fixed-width" :class="[category.icon, category.color]"></span>
                  <div class="d-flex flex-column">
                    <strong class="issue-type-header">{{ category.prompts.length }} {{ category.description }}</strong>
                  </div>
                </div>
                <i class="fa-solid fa-chevron-right chevron"></i>
              </div>

              <div :id="'issue-panel-history-prompts-' + key" class="collapse">
                <div class="details ps-4">
                  <ul class="list-group list-group-flush">
                    <div class="list-group-item rounded issue-item border-0 ps-0"
                         v-for="prompt in category.prompts" :key="prompt.id">
                      <AnswerHistoryItem :prompt="prompt" :category="key"/>
                    </div>
                  </ul>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div v-if="historyData.diff_marks.length === 0 && totalCategorizedPrompts === 0" class="alert alert-info mx-2"
           role="alert">
        <template v-if="historyData.dateOld">
          No Changes Detected.
        </template>
        <template v-else>
          <h4 style="font-size:90%">{{ $filters.dateFilter(historyData.dateNew) }}:</h4> New Page Detected.
        </template>
      </div>

      <div v-if="historyMarks.length > 0">
        <div class="px-3 mb-2 mt-4">
          <small class="text-uppercase fw-bold text-muted" style="letter-spacing: 0.05rem; font-size: 0.7rem;">Visual
            Changes</small>
        </div>
        <div class="list-group list-group-flush" v-if="historyMarks.filter((h) => h.type === 'text').length">
          <div class="list-group-item border-0">
            <div class="issue-header rounded collapsed" data-bs-toggle="collapse" data-bs-target="#issue-panel-history-text">
              <div class="d-flex align-items-center flex-grow-1 issue-text text-truncate">
                <span class="fa fa-square me-1 issue-icon-fixed-width proofreading-text-warning"></span>
                <strong class="issue-type-header">{{ historyMarks.filter((h) => h.type === 'text').length }} Visible
                  Text Changes</strong>
              </div>
              <i class="fa-solid fa-chevron-right chevron"></i>
            </div>

            <div id="issue-panel-history-text" class="collapse">
              <div class="details ps-4">
                <ul class="list-group list-group-flush">
                  <div class="list-group-item list-group-item-action rounded issue-item border-0 ps-0"
                       v-for="historyMark in historyMarks.filter((h) => h.type === 'text')">
                    <div class="history-mark">
                      <span class="badge bg-black-50">&lt;{{ historyMark.node }}&gt;</span>
                      &nbsp;<span class="text-muted">  {{
                        $filters.truncate(historyMark.oldText, 20)
                      }} -&gt; {{ $filters.truncate(historyMark.newText, 20) }}</span>
                      <br>
                      <small class="text-muted">
                        <router-link
                            :to="{query: {'section': 'history', 'history': $route.query.history,  'i': historyMark.id}, force: true}">
                          <span class="fa fa-magnifying-glass"></span> Locate on page
                        </router-link>
                      </small>
                    </div>
                  </div>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div class="list-group list-group-flush"
             v-if="historyMarks.filter((h) => ['difference', 'new'].includes(h.type)).length">
          <div class="list-group-item border-0">
            <div class="issue-header rounded collapsed" data-bs-toggle="collapse" data-bs-target="#issue-panel-history-changes">
              <div class="d-flex align-items-center flex-grow-1 issue-text text-truncate">
                <span class="fa fa-square me-1 issue-icon-fixed-width proofreading-text-warning"></span>
                <strong class="issue-type-header">{{
                    historyMarks.filter((h) => ['difference', 'new'].includes(h.type)).length
                  }} Changed Elements</strong>
              </div>
              <i class="fa-solid fa-chevron-right chevron"></i>
            </div>

            <div id="issue-panel-history-changes" class="collapse">
              <div class="details ps-4">
                <ul class="list-group list-group-flush">
                  <div class="list-group-item list-group-item-action rounded issue-item border-0 ps-0"
                       v-for="historyMark in historyMarks.filter((h) => ['difference', 'new'].includes(h.type))">
                    <div class="history-mark">
                      <span class="badge bg-black-50">&lt;{{ historyMark.node }}&gt;</span>
                      &nbsp;<span class="text-muted">  {{ $filters.truncate(historyMark.newText, 35) }}</span>
                      <br>
                      <small class="text-muted">
                        <router-link
                            :to="{query: {'section': 'history', 'history': $route.query.history,  'i': historyMark.id}, force: true}">
                          <span class="fa fa-magnifying-glass"></span> Locate on page
                        </router-link>
                      </small>
                    </div>
                  </div>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>